import { type LinkedinCookieCredentials, type LinkedinProduct } from '@unipile/provider-linkedin';
import { Alert, AlertDescription, Button, Field, FieldGroup, FieldLabel, Input, LocationConfig, Spinner } from '@unipile/ui';
import { InfoIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InstructionsDialog } from './InstructionsDialog';

type LinkedinPremiumProduct = Extract<LinkedinProduct, 'recruiter' | 'sales_navigator'>;

function getPremiumAccessTokenLabelKey(premiumProducts: LinkedinPremiumProduct[]) {
  if (premiumProducts.includes('recruiter') && premiumProducts.includes('sales_navigator')) {
    return 'LoginScreen.form.premium_access_token.label.recruiter_sales_navigator';
  }
  if (premiumProducts.includes('recruiter')) {
    return 'LoginScreen.form.premium_access_token.label.recruiter';
  }
  return 'LoginScreen.form.premium_access_token.label.sales_navigator';
}

export function CookiesForm({
  onSubmit,
  products,
  isPending,
}: {
  onSubmit: (data: LinkedinCookieCredentials) => void;
  products: LinkedinProduct[];
  isPending: boolean;
}) {
  const form = useForm<LinkedinCookieCredentials>({
    defaultValues: {
      access_token: '',
    },
  });
  const { t } = useTranslation();
  const premiumProducts = products.filter(
    (product): product is LinkedinPremiumProduct => product === 'recruiter' || product === 'sales_navigator',
  );

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-4">
      <Alert>
        <InfoIcon />
        <AlertDescription>{t('LoginScreen.cookies.warning', { ns: 'linkedin' })} </AlertDescription>
        <AlertDescription className="flex items-end justify-end">
          <InstructionsDialog />
        </AlertDescription>
      </Alert>
      <FieldGroup>
        <Controller
          control={form.control}
          name="access_token"
          rules={{ required: t('LoginScreen.form.access_token.required', { ns: 'linkedin' }) }}
          render={({ field }) => (
            <Field>
              <FieldLabel>
                {t('LoginScreen.form.access_token.label', { ns: 'linkedin' })}
                <code className="text-muted-foreground bg-muted ml-1 rounded-md px-1 text-xs">li_at</code>
              </FieldLabel>
              <Input placeholder="AQEDAR4u1DMDGu1YAA..." {...field} />
            </Field>
          )}
        />
        {premiumProducts.length > 0 && (
          <Controller
            control={form.control}
            name="premium_access_token"
            render={({ field }) => (
              <Field>
                <FieldLabel>
                  {t(getPremiumAccessTokenLabelKey(premiumProducts), { ns: 'linkedin' })}{' '}
                  <code className="text-muted-foreground bg-muted rounded-md px-1 text-xs">li_a</code>{' '}
                  <span className="text-muted-foreground">
                    ({t('LoginScreen.form.premium_access_token.optional', { ns: 'linkedin' })})
                  </span>
                </FieldLabel>
                <Input {...field} />
              </Field>
            )}
          />
        )}

        <LocationConfig provider="linkedin" />
        <Button disabled={isPending} type="submit">
          {isPending && <Spinner />}
          {t('Common.next')}
        </Button>
      </FieldGroup>
    </form>
  );
}
